import "@/App.css";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";

// Pages
import Dashboard from "./pages/Dashboard";
import Organizations from "./pages/Organizations";
import OrganizationDetail from "./pages/OrganizationDetail";
import OrganizationForm from "./pages/OrganizationForm";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import ContactForm from "./pages/ContactForm";
import Venues from "./pages/Venues";
import VenueDetail from "./pages/VenueDetail";
import VenueForm from "./pages/VenueForm";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import EventForm from "./pages/EventForm";
import GlobalSchedule from "./pages/GlobalSchedule";
import TruckingSchedule from "./pages/TruckingSchedule";
import CalendarPage from "./pages/CalendarPage";
import Settings from "./pages/Settings";
import AdminPanel from "./pages/AdminPanel";
import BugReports from "./pages/BugReports";
import FeatureRequests from "./pages/FeatureRequests";
import DispatchDisplay from "./pages/DispatchDisplay";
import DailyDispatchDisplay from "./pages/DailyDispatchDisplay";

// Protected Route wrapper — Cloudflare Access has already authenticated the
// request; a null user here means /auth/me failed (e.g. not provisioned yet).
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground gap-2">
        <h1 className="text-xl font-semibold">Couldn't load your EMP account</h1>
        <p className="text-muted-foreground text-sm">You're signed in, but the EMP API didn't recognize you. Ping Dan.</p>
      </div>
    );
  }

  return <Layout>{children}</Layout>;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Organizations */}
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <Organizations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations/new"
        element={
          <ProtectedRoute>
            <OrganizationForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations/:id"
        element={
          <ProtectedRoute>
            <OrganizationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations/:id/edit"
        element={
          <ProtectedRoute>
            <OrganizationForm />
          </ProtectedRoute>
        }
      />

      {/* Contacts */}
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <Contacts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/new"
        element={
          <ProtectedRoute>
            <ContactForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/:id"
        element={
          <ProtectedRoute>
            <ContactDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/:id/edit"
        element={
          <ProtectedRoute>
            <ContactForm />
          </ProtectedRoute>
        }
      />

      {/* Locations (formerly Venues) */}
      <Route
        path="/locations"
        element={
          <ProtectedRoute>
            <Venues />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/new"
        element={
          <ProtectedRoute>
            <VenueForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/:id"
        element={
          <ProtectedRoute>
            <VenueDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations/:id/edit"
        element={
          <ProtectedRoute>
            <VenueForm />
          </ProtectedRoute>
        }
      />
      {/* Legacy venue routes redirect to locations */}
      <Route path="/venues" element={<Navigate to="/locations" replace />} />
      <Route path="/venues/new" element={<Navigate to="/locations/new" replace />} />
      <Route path="/venues/:id" element={<Navigate to="/locations/:id" replace />} />
      <Route path="/venues/:id/edit" element={<Navigate to="/locations/:id/edit" replace />} />

      {/* Events */}
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <Events />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/new"
        element={
          <ProtectedRoute>
            <EventForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:id"
        element={
          <ProtectedRoute>
            <EventDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:id/edit"
        element={
          <ProtectedRoute>
            <EventForm />
          </ProtectedRoute>
        }
      />

      {/* Global Schedule */}
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <GlobalSchedule />
          </ProtectedRoute>
        }
      />

      {/* Trucking Schedule */}
      <Route
        path="/trucking"
        element={
          <ProtectedRoute>
            <TruckingSchedule />
          </ProtectedRoute>
        }
      />

      {/* Dispatch Display (Full Screen - No Layout) */}
      <Route
        path="/dispatch"
        element={<DispatchDisplay />}
      />

      {/* Daily Dispatch Display (Full Screen - No Layout) */}
      <Route
        path="/dispatch/daily"
        element={<DailyDispatchDisplay />}
      />

      {/* Calendar */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Admin Panel */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />

      {/* Bug Reports (Admin) */}
      <Route
        path="/admin/bugs"
        element={
          <AdminRoute>
            <BugReports />
          </AdminRoute>
        }
      />

      {/* Feature Requests */}
      <Route
        path="/feature-requests"
        element={
          <ProtectedRoute>
            <FeatureRequests />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: "40px", textAlign: "center", color: "#fff", background: "#1a1a2e", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <h1 style={{ fontSize: "24px", marginBottom: "12px" }}>Something went wrong</h1>
          <p style={{ color: "#999", marginBottom: "20px" }}>We've been notified and are working on a fix.</p>
          <button onClick={() => window.location.href = "/emp/"} style={{ padding: "10px 24px", background: "#38c6d9", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}>
            Return to Dashboard
          </button>
        </div>
      }
    >
      <BrowserRouter basename="/emp">
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
